<template>
	<div @keyup="handleKeyPress">
		<v-card>
			<v-card-title>
				<h1 class="text-h4">
					<translate>Search</translate>
				</h1>
			</v-card-title>
			<v-card-text>
				<v-row>
					<v-col>
						<h2>
							<translate>General</translate>
						</h2>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.policyNumber"
							class="mt-0 pt-0"
							:rules="[v => /^([\d/]*)$/.test(v) || !v]"
							:label="$gettext('Policy number')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.claimNumber"
							class="mt-0 pt-0"
							:rules="[v => /^([\d/]*)$/.test(v) || !v]"
							:label="$gettext('Claim number')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.orderReference"
							class="mt-0 pt-0"
							:label="$gettext('Belsimpel/Reseller order number')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.invoiceNumber"
							class="mt-0 pt-0"
							:rules="[v => /^([\d/]*)$/.test(v) || !v]"
							:label="$gettext('Invoice number')"
							variant="outlined"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="py-0">
						<v-select
							v-if="options.products"
							v-model="fields.products"
							class="mt-0 pt-0"
							:label="$gettext('Insurance')"
							:items="options.products"
							closable
							multiple
							chips
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-select
							v-if="options.statuses"
							v-model="fields.statuses"
							class="mt-0 pt-0"
							:label="$gettext('Status')"
							:items="options.statuses"
							item-value="id"
							item-title="name"
							closable
							multiple
							chips
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-select
							v-if="months"
							v-model="fields.months"
							class="mt-0 pt-0"
							:label="$gettext('Month')"
							:items="months"
							item-title="name"
							item-value="number"
							closable
							multiple
							chips
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.year"
							class="mt-0 pt-0"
							:rules="[v => /^(\d{4})$/.test(v) || !v]"
							:label="$gettext('Year')"
							variant="outlined"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="pt-0">
						<device-name-search
							ref="deviceSearch"
							v-model="fields.deviceId"
							class="mt-0 pt-0"
							:label="$gettext('Device name')"
							small-chips
							variant="outlined"
						/>
					</v-col>
					<v-col class="pt-0">
						<v-text-field
							v-model="fields.imei"
							class="mt-0 pt-0"
							:rules="[v => /^\d{0,15}$/.test(v) || !v]"
							:label="$gettext('IMEI')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pt-0">
						<v-text-field
							v-model="fields.ean"
							class="mt-0 pt-0"
							:rules="[v => /^\d{0,13}$/.test(v) || !v]"
							:label="$gettext('EAN')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pt-0" />
				</v-row>
				<v-row>
					<v-col>
						<h2>
							<translate>Customer Information</translate>
						</h2>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.userId"
							clearable
							class="mt-0 pt-0"
							:rules="[v => /^[\d/]*$/.test(v) || !v]"
							:label="$gettext('Customer number')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.initials"
							class="mt-0 pt-0"
							:label="$gettext('Initials')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pb-0">
						<v-text-field
							v-model="fields.lastName"
							class="mt-0 pt-0"
							:label="$gettext('Last name')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pb-0">
						<v-combobox
							v-model="birthDates"
							:label="$gettext('Date of birth')"
							:delimiters="[' ']"
							prepend-inner-icon="$calendar"
							class="mt-0 pt-0"
							multiple
							chips
							placeholder="DD-MM-YYYY"
							append-icon=""
							:rules="[validate]"
							variant="outlined"
						>
							<template #chip="{ attrs, item, select, selected }">
								<v-chip
									size="small"
									v-bind="attrs"
									:model-value="selected"
									@click="select"
								>
									{{ item.value }}
								</v-chip>
							</template>
						</v-combobox>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.email"
							class="mt-0 pt-0"
							:label="$gettext('Email address')"
							:rules="[v => emailRegex.test(v) || !v]"
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.phoneNumber"
							class="mt-0 pt-0"
							:rules="[v => /^[+]?\d*$/.test(v) || !v]"
							variant="outlined"
						>
							<template #label>
								<div>
									<translate>Phone number</translate> <small>(e.g. +31612345678)</small>
								</div>
							</template>
							<template #append-inner>
								<v-tooltip
									location="bottom"
								>
									<template #activator="{ props }">
										<v-icon
											color="primary"
											v-bind="props"
											icon="$info"
										/>
									</template>
									<span><translate>If no country code is specified, the number is assumed to be Dutch</translate> (e.g. 0612345678)</span>
								</v-tooltip>
							</template>
						</v-text-field>
					</v-col>
					<v-col class="py-0">
						<v-select
							v-if="options.languages"
							v-model="fields.languages"
							class="mt-0 pt-0"
							:label="$gettext('Language')"
							:items="options.languages"
							item-value="id"
							item-title="name"
							chips
							closable
							multiple
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-select
							v-if="options.domains"
							v-model="fields.domains"
							class="mt-0 pt-0"
							:label="$gettext('Domain')"
							:items="options.domains"
							item-value="id"
							item-title="name"
							chips
							closable
							multiple
							variant="outlined"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.street"
							class="mt-0 pt-0"
							:label="$gettext('Street')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.houseNumber"
							class="mt-0 pt-0"
							:label="$gettext('House number')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.postalCode"
							class="mt-0 pt-0"
							:label="$gettext('Postal code')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="py-0">
						<v-text-field
							v-model="fields.country"
							class="mt-0 pt-0"
							:label="$gettext('Country')"
							variant="outlined"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="pt-0">
						<v-text-field
							v-model="fields.iban"
							class="mt-0 pt-0"
							:label="$gettext('IBAN')"
							:rules="[v => ibanRegex.test(v) || !v]"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pt-0">
						<v-text-field
							v-model="fields.businessName"
							class="mt-0 pt-0"
							:label="$gettext('Business name')"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pt-0">
						<v-text-field
							v-model="fields.businessKvk"
							class="mt-0 pt-0"
							:label="$gettext('COC number')"
							:rules="[v => /^\d*$/.test(v) || !v]"
							variant="outlined"
						/>
					</v-col>
					<v-col class="pt-0" />
				</v-row>
			</v-card-text>
			<v-card-actions>
				<v-row>
					<v-col class="text-right">
						<v-btn
							color="primary"
							@click="clearFields"
						>
							<translate>Clear all fields</translate>
						</v-btn>
						<v-btn
							color="primary"
							@click="submitSearch"
						>
							<translate>Search</translate>
						</v-btn>
					</v-col>
				</v-row>
			</v-card-actions>
		</v-card>
		<br>
		<v-card v-if="loading || results">
			<v-card-title>
				<translate>Results</translate>
			</v-card-title>
			<v-card-text>
				<v-progress-linear
					v-if="loading"
					indeterminate
					color="primary"
				/>
				<v-data-table
					v-if="results"
					v-model:expanded="expanded"
					:headers="headers"
					:items-length="results.length"
					:items="results"
					item-value="identifier"
					show-expand
				>
					<template #item.identifier="{ item }">
						<a
							:href="'orders/view?id=' + item.orderId"
							target="_blank"
						>{{ item.identifier }}</a>
					</template>
					<template #item.fullName="{ item }">
						<a
							:href="'users/view?id=' + item.userId"
							target="_blank"
						>{{ item.fullName }}</a>
					</template>
					<template #item.dob="{ item }">
						{{ item.dob ? item.dob.toLocaleDateString('en-NL') : "" }}
					</template>
					<template #item.startDate="{ item }">
						{{ item.startDate ? item.startDate.toLocaleDateString('en-NL') : "" }}
					</template>
					<template #item.endDate="{ item }">
						{{ item.endDate ? item.endDate.toLocaleDateString('en-NL') : "" }}
					</template>
					<template #item.activePolicy="{ item }">
						<v-tooltip :text="$gettext(item.activePolicy)">
							<template #activator="{ props }">
								<v-icon
									v-if="item.activePolicy === 'active insurance'"
									icon="$check"
									color="primary"
									v-bind="props"
								/>
								<v-icon
									v-else-if="item.activePolicy === 'insurance terminated'"
									icon="$cancel"
									color="error"
									v-bind="props"
								/>
								<template v-else>
									<v-icon
										icon="$hourGlass"
										v-bind="props"
									/>
									<v-icon
										icon="$cancel"
										v-bind="props"
									/>
								</template>
							</template>
						</v-tooltip>
					</template>
					<template #column.data-table-expand>
						<translate>Claims</translate>
					</template>
					<template #item.data-table-expand="{ item }">
						<div v-if="item.claims">
							<v-btn
								v-if="!expanded.includes(item.identifier)"
								key="expand"
								variant="text"
								@click="expand(item)"
							>
								<v-icon
									color="primary"
									icon="$chevronDown"
								/>
							</v-btn>
							<v-btn
								v-else
								key="condense"
								variant="text"
								@click="expanded = []"
							>
								<v-icon
									color="primary"
									icon="$chevronUp"
								/>
							</v-btn>
						</div>
					</template>
					<template #expanded-row="{ item }">
						<tr>
							<th
								v-for="header in headerClaims"
								:key="header.key"
								colspan="2"
								class="pl-5"
							>
								{{ header.title }}
							</th>
						</tr>
						<tr
							v-for="claim in item.claims"
							:key="claim.id"
						>
							<td colspan="2">
								<a
									:href="'claims/view?id=' + claim.id"
									target="_blank"
								>{{ claim.identifier }}</a>
							</td>
							<td colspan="2">
								{{ claim.startDate }}
							</td>
							<td colspan="2">
								{{ claim.endDate }}
							</td>
							<td colspan="2">
								{{ claim.type }}
							</td>
							<td colspan="2">
								{{ claim.claimStatus }}
							</td>
						</tr>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>
	</div>
</template>
<script>
import {
	axios,
} from '../../../../services/axios-service';
import {
	displayError,
} from 'laravel-front-library';
import {
	isNull,
} from 'lodash';

export default {
	name: 'BackOfficeSearch',
	data () {
		return {
			fields: {
				policyNumber: null,
				claimNumber: null,
				orderReference: null,
				invoiceNumber: null,
				products: null,
				statuses: null,
				months: null,
				year: null,
				domains: null,
				imei: null,
				ean: null,
				deviceId: null,
				userId: null,
				initials: null,
				lastName: null,
				dobs: null,
				email: null,
				phoneNumber: null,
				street: null,
				houseNumber: null,
				postalCode: null,
				country: null,
				languages: null,
				iban: null,
				businessName: null,
				businessKvk: null,
			},

			options: {
				products: null,
				statuses: null,
				domains: null,
				languages: null
			},

			deviceLoading: false,
			birthDates: [],

			loading: false,
			results: null,
			expanded: [],
			emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			ibanRegex: /^(?:(?:IT|SM)\d{2}[A-Z]\d{22}|CY\d{2}[A-Z]\d{23}|NL\d{2}[A-Z]{4}\d{10}|LV\d{2}[A-Z]{4}\d{13}|(?:BG|BH|GB|IE)\d{2}[A-Z]{4}\d{14}|GI\d{2}[A-Z]{4}\d{15}|RO\d{2}[A-Z]{4}\d{16}|KW\d{2}[A-Z]{4}\d{22}|MT\d{2}[A-Z]{4}\d{23}|NO\d{13}|(?:DK|FI|GL|FO)\d{16}|MK\d{17}|(?:AT|EE|KZ|LU|XK)\d{18}|(?:BA|HR|LI|CH|CR)\d{19}|(?:GE|DE|LT|ME|RS)\d{20}|IL\d{21}|(?:AD|CZ|ES|MD|SA)\d{22}|PT\d{23}|(?:BE|IS)\d{24}|(?:FR|MR|MC)\d{25}|(?:AL|DO|LB|PL)\d{26}|(?:AZ|HU)\d{27}|(?:GR|MU)\d{28})$/

		};
	},

	computed: {
		months () {
			const monthNos = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, ];

			return monthNos.map((monthNo) => {
				return {
					number: monthNo,
					name: this.numberToMonth(monthNo)
				};
			});
		},

		headers () {
			return [
				{
					title: this.$gettext('Policy number'), value: 'identifier'
				},
				{
					title: this.$gettext('Start date'), value: 'startDate'
				},
				{
					title: this.$gettext('End date'), value: 'endDate'
				},
				{
					title: this.$gettext('Active policy'), value: 'activePolicy'
				},
				{
					title: this.$gettext('Customer name'), value: 'fullName'
				},
				{
					title: this.$gettext('Date of birth'), value: 'dob'
				},
				{
					title: this.$gettext('Postal code'), value: 'postalCode'
				},
				{
					title: this.$gettext('House number'), value: 'houseNumber'
				},
				{
					title: this.$gettext('Insurance'), value: 'product'
				},
				{
					title: this.$gettext('Status'), value: 'status'
				},
			];
		},

		headerClaims () {
			return [
				{
					title: this.$gettext('Claim number'), value: 'identifier'
				},
				{
					title: this.$gettext('Start Date'), value: 'startDate'
				},
				{
					title: this.$gettext('End Date'), value: 'endDate'
				},
				{
					title: this.$gettext('Type'), value: 'type'
				},
				{
					title: this.$gettext('Status'), value: 'claimStatus'
				},
			];
		}
	},

	created () {
		this.getDropdownOptions();

		this.fields.phoneNumber = (new URLSearchParams(window.location.search.substring(1))).get('phone_number');
		if (!isNull(this.fields.phoneNumber)) {
			this.submitSearch();
		}

		window.addEventListener('keyup', this.handler);
	},

	beforeUnmount () {
		window.removeEventListener('keyup', this.handler);
	},

	methods: {

		/**
		 * Validation for the date of birth field
		 *
		 * @param {Array} i
		 * @returns {boolean|boolean|string}
		 */
		validate (i) {
			return (/^((0[1-9]|[12][0-9]|3[01])[/-](0[1-9]|1[012])[/-]((19|20)\d{2}) ?)+$/.test(i.join(' '))) || !i || !i.length || this.$gettext('Please fill in a valid date as DD-MM-YYYY');
		},

		/**
		 * Format the array of dates from dd-mm-yyyy to yyyy-mm-dd
		 *
		 * @returns {Array} - array of reformatted dates
		 */
		formatDatesToYearFirst () {
			const newDates = [];

			this.birthDates.forEach((date) => {
				const [day, month, year, ] = date.split('-');

				newDates.push(`${year}-${month}-${day}`);
			});
			return newDates;
		},

		/**
		 * Get the available options for each of the search fields that have multiple choice options
		 *
		 * @returns {void}
		 */
		getDropdownOptions () {
			axios.get('/api/v0/products').then(({
				data: products
			}) => {
				const allProducts = products.map((product) => product.name);

				this.options.products = [...new Set(allProducts), ];
				const allDomains = products.map((product) => {
					return {
						id: product.domain.id,
						name: product.domain.name,
					};
				});

				this.options.domains = allDomains.filter((value, index, self) =>
					self.findIndex((v) => v.id === value.id && v.name === value.name) === index
				);
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.Sentry.captureException(error);
			});

			axios.get('/api/v0/languages/supported').then(({
				data: languages
			}) => {
				this.options.languages = languages.map((language) => {
					return {
						id: language.id,
						name: this.$gettext(language.name)
					};
				});
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.Sentry.captureException(error);
			});

			axios.get('/api/v0/orderrowstatuses').then(({
				data: orderrowStatuses
			}) => {
				this.options.statuses = orderrowStatuses.map((orderrowStatus) => {
					return {
						id: orderrowStatus.id,
						name: orderrowStatus.name
					};
				});
			});
		},

		/**
		 * Get the name of a month in the current language, given the number of the month
		 *
		 * @param {number} monthNo
		 * @returns {string}
		 */
		numberToMonth (monthNo) {
			return (new Date(2022, monthNo - 1)).toLocaleDateString(this.$language.toLowerCase().replace('_', '-'), {
				month: 'long'
			});
		},

		/**
		 * Set all of the search fields to back to empty
		 *
		 * @returns {void}
		 */
		clearFields () {
			this.fields = {
				policyNumber: null,
				claimNumber: null,
				orderReference: null,
				products: null,
				statuses: null,
				months: null,
				year: null,
				domains: null,
				imei: null,
				deviceId: null,
				userId: null,
				initials: null,
				lastName: null,
				dobs: null,
				email: null,
				phoneNumber: null,
				street: null,
				houseNumber: null,
				postalCode: null,
				country: null,
				languages: null,
				iban: null,
				businessName: null,
				businessKvk: null,
			};
			this.birthDates = [];
			this.$refs.deviceSearch.reset();
		},

		/**
		 * Execute the search given the current search fields
		 *
		 * @returns {void}
		 */
		submitSearch () {
			this.loading = true;
			this.expanded = [];
			this.fields.dobs = this.formatDatesToYearFirst();
			axios.get('/api/v0/users/search', {
				params: this.fields,
			}).then(({
				data: results
			}) => {
				this.toDates(results);
				this.results = results;
				if (this.results.length === 100) {
					displayError(this.$gettext('Too many results (Limited to 100 entries)'));
				}
				if (!isNull(this.fields.claimNumber) && this.fields.claimNumber.length > 0) {
					this.results.map((orderRow) => {
						if (orderRow.claims.some((claim) => {
							return claim.identifier === this.fields.claimNumber;
						})) {
							this.expand(orderRow);
						}
						return orderRow;
					});
				}
			}).catch((error) => {
				if (error.response && error.response.status === 400) {
					displayError(error.response.data.error);
				} else {
					if (error.message && error.message === 'Request aborted') {
						return;
					}
					displayError(this.$gettext('Something has gone wrong during data retrieval'));
					window.Sentry.captureException(error);
				}
			}).finally(() => {
				this.loading = false;
			});
		},

		/**
		 * Convert dates in a list of order rows to javascript date objects
		 *
		 * @param {Array} orderRows
		 * @returns {void}
		 */
		toDates (orderRows) {
			orderRows.map((orderRow) => {
				orderRow.startDate = orderRow.startDate !== '' ? new Date(orderRow.startDate) : orderRow.startDate;
				orderRow.endDate = orderRow.endDate !== '' ? new Date(orderRow.endDate) : orderRow.endDate;
				orderRow.dob = orderRow.dob !== '' ? new Date(orderRow.dob) : orderRow.dob;
				return orderRow;
			});
		},

		/**
		 * Submit the search query if the enter key is pressed
		 *
		 * @param {object} e
		 * @returns {void}
		 */
		handleKeyPress (e) {
			if (e.which === 13) {
				this.submitSearch();
			}
		},
		/**
		 * @param {object} item
		 */
		expand (item) {
			this.expanded = [item.identifier, ];
		}
	}
};
</script>

<style scoped lang="less">
	.v-select.v-select--chips.v-select--chips--small ::v-deep.v-select__selections {
		min-height: 32px;
	}
</style>
